import { AlertColor, Box, Button, TextField, Typography, useTheme } from '@mui/material';
import strings from 'common/Translation/Translate';
import { FormDialog } from 'components/Dialog/FormDialog';
import Notification from 'components/Notification';
import { useState } from 'react';
import axiosInstance from 'utils/axios';
import { URLS } from 'utils/constants/urls';
import { regex } from 'utils/validators/Validators';

type CompleteTaskDialogProps = {
  id: any;
  open: boolean;
  onClose: () => void;
  onSuccess: any;
};

export const CompleteTaskDialog: React.FC<CompleteTaskDialogProps> = ({ id, open, onClose, onSuccess }) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [comments, setComments] = useState<string>('');
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const maxLength = 250;

  const isCommentsValid = () => {
    return comments.length > 0 && comments.length <= maxLength && regex.string.test(comments);
  };

  const handleClose = () => {
    setComments('');
    onClose();
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      if (!isCommentsValid()) {
        setMessage({
          display: true,
          severity: 'warning',
          message: strings.requiredText,
        });
        return;
      }

      await axiosInstance.post(URLS.completeTask(id), { completionComment: comments });

      setMessage({
        display: true,
        severity: 'success',
        message: strings.bookingCancelSuccess,
      });
      handleClose();
      onSuccess();
    } catch (error: any) {
      setIsLoading(false);
      handleClose();
      if (error.response) {
        setMessage({
          display: true,
          severity: 'error',
          message: error.response.data?.error_description ?? error.response.data.message,
        });
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: error.message,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <FormDialog
        data-testid={'dialog'}
        open={open}
        handleClose={handleClose}
        title={strings.markCompleteBtn}
        contentText={'Remarks'}
        loading={isLoading}
        handleSave={handleSave}
        disabledCondition={!isCommentsValid()}>
        <TextField
          required
          multiline
          maxRows={6}
          minRows={6}
          value={comments}
          error={!isCommentsValid()}
          helperText={
            <Box display="flex" justifyContent={!isCommentsValid() ? 'space-between' : 'flex-end'} sx={{ mt: 1 }}>
              {!regex.string.test(comments) && (
                <Typography fontSize={'12px'} color="error" sx={{ fontWeight: 500 }}>
                  {strings.validateStringErrMsg}
                </Typography>
              )}

              {comments.length === 0 ? (
                <Typography fontSize={'12px'} color="error" sx={{ fontWeight: 500 }}>
                  {strings.requiredText}
                </Typography>
              ) : (
                <Typography
                  fontSize={'12px'}
                  color={comments.length > maxLength ? 'error' : 'text.secondary'}
                  sx={{ fontWeight: 500 }}>
                  {`${comments?.length}/${maxLength}`}
                </Typography>
              )}
            </Box>
          }
          onChange={e => setComments(e.target.value)}
          sx={{
            width: '100%',
            mt: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              transition: 'all 0.2s ease-in-out',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderWidth: 2,
                borderColor: theme.palette.primary.main,
              },
            },
            '& .MuiFormHelperText-root': {
              textAlign: 'end',
              m: 0,
            },
          }}
        />
      </FormDialog>
    </>
  );
};

type MarkCompleteButtonProps = {
  id: any;
  onSuccess: any;
};

export const MarkCompleteButton: React.FC<MarkCompleteButtonProps> = ({ id, onSuccess }) => {
  const [openDialog, setOpenDialog] = useState(false);
  return (
    <>
      <Button
        data-testid={'btn'}
        sx={{
          width: 'max-content',
          minWidth: '100px',
          height: '28px',
          padding: '0 12px',
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.75rem',
        }}
        variant="outlined"
        onClick={() => setOpenDialog(true)}
        color="success">
        {strings.markCompleteBtn}
      </Button>
      <CompleteTaskDialog id={id} open={openDialog} onClose={() => setOpenDialog(false)} onSuccess={onSuccess} />
    </>
  );
};

